import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Grid, InputAdornment, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import { getMasters, getPaymentMethodReference } from '../../services/platform/masterService';
import {
  PAYMENT_METHOD_LABELS,
  allowedGatewaysForMethod,
  nextPaymentMethodSortOrder,
  normalizePaymentMethodName,
  paymentMethodCodeFromName,
  validatePaymentMethodForm,
  type PaymentMethodExistingRecord,
} from '../../utils/paymentMethodMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const lockAdornment = {
  readOnly: true,
  endAdornment: (
    <InputAdornment position="end">
      <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
    </InputAdornment>
  ),
};

export default function PaymentMethodFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const meta = form.metadata;

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['payment-method-reference'],
    queryFn: getPaymentMethodReference,
    enabled: open,
    staleTime: 0,
  });
  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'payment_methods', 'form-siblings'],
    queryFn: () => getMasters('payment_methods', { limit: 500, page: 1 }),
    enabled: open,
    staleTime: 0,
  });
  const existing = useMemo<PaymentMethodExistingRecord[]>(
    () => ((existingRaw?.data ?? []) as PaymentMethodExistingRecord[]),
    [existingRaw],
  );
  const gateways = useMemo(() => reference?.gateways ?? [], [reference]);
  const maxFeePercent = reference?.maxFeePercent ?? 20;
  const suggestedSortOrder = useMemo(() => nextPaymentMethodSortOrder(existing, editingId), [existing, editingId]);
  const editingRow = existing.find((r) => r._id === editingId);
  const allowedGateways = allowedGatewaysForMethod(str(form.code), gateways);
  const gateway = gateways.find((g) => g.value === str(meta.gateway));
  const fee = str(meta.feePercent) === '' ? 0 : Number(meta.feePercent);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', isActive: true, sortOrder: 0, metadata: { gateway: '', feePercent: 0, feeBasis: 'none' } });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(() => {
    const result = validatePaymentMethodForm(
      { code: form.code, name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { existing, editingId, gateways, maxFeePercent },
    );
    if (editing && form.isActive !== false && gateway && !gateway.ready && !result.gateway) {
      result.gateway = `${gateway.reason} — pick a ready gateway, or deactivate this method in the list first`;
    }
    return result;
  }, [form.code, form.name, form.description, form.sortOrder, form.isActive, meta, existing, editingId, gateways, maxFeePercent, editing, gateway]);
  const blocking = Object.keys(errors).map((key) => [key, PAYMENT_METHOD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading && !referenceLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const setMeta = (patch: Record<string, string | number | boolean>, extra: Partial<MasterFormState> = {}) => {
    onChange({ ...form, ...extra, metadata: { ...meta, ...patch } });
  };

  const handleNameChange = (raw: string) => {
    const name = limitConsecutiveRepeatedCharacters(raw).replace(/^\s+/, '');
    if (editing) {
      onChange({ ...form, name });
    } else {
      const code = paymentMethodCodeFromName(normalizePaymentMethodName(name));
      const allowed = allowedGatewaysForMethod(code, gateways);
      const current = str(meta.gateway);
      const nextGateway = allowed.length === 1 ? allowed[0].value : allowed.some((g) => g.value === current) ? current : '';
      setMeta({ gateway: nextGateway }, { name, code });
    }
    touch('name');
  };

  const handleGatewayChange = (value: string) => {
    const def = gateways.find((g) => g.value === value);
    const patch: Record<string, string | number | boolean> = { gateway: value };
    if (def?.kind === 'offline' && fee > 0) patch.feeBasis = 'platform';
    setMeta(patch);
    touch('gateway');
  };

  const handleFeeChange = (raw: string) => {
    if (/[-+eE,]/.test(raw)) return;
    const value = raw === '' ? '' : raw;
    const num = Number(value || 0);
    const patch: Record<string, string | number | boolean> = { feePercent: value === '' ? '' : num };
    if (!num) patch.feeBasis = 'none';
    else if (str(meta.feeBasis) === 'none' || !str(meta.feeBasis)) patch.feeBasis = gateway?.kind === 'offline' ? 'platform' : '';
    setMeta(patch);
    touch('feePercent');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const actionLabel = editing ? 'Update' : 'Create';
  const basisDefs = reference?.feeBases ?? [];
  const basis = str(meta.feeBasis);
  const basisHint = basisDefs.find((b) => b.value === basis)?.hint;
  const willBeInactive = !editing && gateway && !gateway.ready;
  const deleteNote = editingRow?.deleteLock;

  const feeSummary = !fee
    ? 'No fee — customers pay the order total only.'
    : basis === 'platform'
      ? `Customers pay ${fee}% extra, kept by the platform as revenue.`
      : basis === 'gateway'
        ? `Customers pay ${fee}% extra to recover the ${gateway?.label ?? 'gateway'} processing charge.`
        : basis === 'both'
          ? `Customers pay ${fee}% extra, covering the ${gateway?.label ?? 'gateway'} charge plus a platform margin.`
          : `Choose what the ${fee}% fee covers.`;

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Payment Method`}
      subtitle="A payment option customers can choose at checkout, linked to the gateway that processes it."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={640}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Complete these to enable {actionLabel}:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {blocking.map(([key, label]) => (
              <Chip
                key={key}
                size="small"
                label={`${label}: ${errors[key]}`}
                color="warning"
                variant="outlined"
                onClick={() => focusField(key)}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
              />
            ))}
          </Stack>
        </Alert>
      )}
      {editing && deleteNote && (
        <Alert severity="info" sx={{ mb: 2 }}>{deleteNote}.</Alert>
      )}

      <FormSection title="Basic info" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => {
                touch('name');
                const clean = normalizePaymentMethodName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Shown at checkout · letters only (e.g. Net Banking)'}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="code">
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={str(form.code)}
              placeholder="Generated from the name"
              error={!!errors.code && (touched.has('name') || /already/.test(errors.code))}
              helperText={(touched.has('name') || /already/.test(errors.code || '') ? errors.code : undefined)
                || (editing ? 'Locked — orders store this code' : 'Auto-generated from the name · unique')}
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace', fontSize: 13 } }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Position at checkout · unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Gateway & fees">
        <FormGrid>
          <Grid item xs={12} data-field="gateway">
            <TextField
              select
              fullWidth
              required
              size="small"
              label="Payment gateway"
              value={str(meta.gateway)}
              disabled={!str(form.code) || allowedGateways.length === 1}
              onChange={(e) => handleGatewayChange(e.target.value)}
              onBlur={() => touch('gateway')}
              error={!!errorFor('gateway')}
              helperText={errorFor('gateway')
                || (!str(form.code)
                  ? 'Enter the name first — the allowed gateways depend on the method'
                  : allowedGateways.length === 1
                    ? `Fixed — ${allowedGateways[0].label} methods are always processed by this gateway`
                    : gateway
                      ? gateway.ready
                        ? `${gateway.label} is configured and enabled${gateway.sandbox ? ' (sandbox mode)' : ''}`
                        : gateway.reason
                      : 'Online methods need an online gateway configured under Settings → Payment Gateways')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (v) => (v
                  ? gateways.find((g) => g.value === v)?.label ?? String(v)
                  : <Typography component="span" color="text.secondary">— Select —</Typography>),
              }}
            >
              {allowedGateways.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{g.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {g.ready ? (g.kind === 'offline' ? 'Built-in — no setup needed' : `Configured${g.sandbox ? ' · sandbox' : ''}`) : g.reason}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={g.ready ? 'Ready' : 'Not ready'}
                      color={g.ready ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Gateways are set up in{' '}
              <Link component={RouterLink} to="/config/payments" underline="hover">Settings → Payment Gateways</Link>.
              A method can only be active while its gateway is enabled and has credentials.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5} data-field="feePercent">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Fee %"
              value={meta.feePercent ?? ''}
              onChange={(e) => handleFeeChange(e.target.value)}
              onBlur={() => touch('feePercent')}
              error={!!errorFor('feePercent')}
              helperText={errorFor('feePercent') || `0–${maxFeePercent} · up to 2 decimals · 0 = no fee`}
              inputProps={{ min: 0, max: maxFeePercent, step: 0.01 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={7} data-field="feeBasis">
            <TextField
              select
              fullWidth
              size="small"
              required={fee > 0}
              label="Fee basis"
              value={fee > 0 ? (basis === 'none' ? '' : basis) : 'none'}
              disabled={!fee}
              onChange={(e) => { setMeta({ feeBasis: e.target.value }); touch('feeBasis'); }}
              onBlur={() => touch('feeBasis')}
              error={!!errorFor('feeBasis')}
              helperText={errorFor('feeBasis') || (fee ? basisHint || 'What the fee is charged for' : 'Set a fee % to choose what it covers')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="none" disabled>No fee</MenuItem>
              {fee > 0 && !basis && <MenuItem value="" disabled>— Select —</MenuItem>}
              {basisDefs.map((b) => (
                <MenuItem key={b.value} value={b.value} disabled={gateway?.kind === 'offline' && b.value !== 'platform'}>
                  <Box>
                    <Typography variant="body2">{b.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{b.hint}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">{feeSummary}</Typography>
          </Grid>
          <Grid item xs={12}>
            {willBeInactive ? (
              <Alert severity="warning">
                {gateway!.reason}. The method will be saved as <strong>Inactive</strong> — activate it from the list once the gateway is ready.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {editing
                  ? 'Use the status toggle in the Payment Methods list to activate or deactivate this method.'
                  : 'New methods are created as Active — use the status toggle in the list to deactivate later.'}
              </Typography>
            )}
          </Grid>
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
